import { FV, geometricMean, PmtAnnuityDue, PV } from '../financeMath';
import { getRecurrenceConfig, Recurrence } from '../types';
import { dt } from '../util/dateTime';
import { getUnitsPerYear } from '../valueSeries/ticks';
import { getNumPeriods } from './helpers';

export const calculatePaymentAmount = ({
    targetAmount,
    targetDate,
    startAmount,
    startDate,
    annualGrowthRate,
    recurrence: { freq, interval },
    recurrenceStartDate,
}: {
    startAmount?: number | null;
    targetAmount: number;
    targetDate: string;
    startDate: string;
    annualGrowthRate: number;
    recurrence: Recurrence;
    recurrenceStartDate?: string | null;
}): number => {
    const daysToTarget = getNumPeriods({ startDate, targetDate, unit: 'day' });
    const dailyInterestRate = geometricMean(
        annualGrowthRate,
        getUnitsPerYear('day')
    );

    const fvStartAmount = FV({
        startingBalance: startAmount ?? 0,
        interestRate: dailyInterestRate,
        numPeriods: daysToTarget,
    });

    let targetAmountDiff = targetAmount - fvStartAmount;

    // If startAmount will fully cover targetAmount on targetDate,
    // no payment is necessary.
    if (targetAmountDiff <= 0) return 0;

    const { perYear, unit } = getRecurrenceConfig({ freq, interval });
    const periodReturn = geometricMean(annualGrowthRate, perYear);

    const numPeriods = getNumPeriods({
        startDate: recurrenceStartDate ?? startDate,
        targetDate,
        unit,
        interval,
    });
    const numDeposits =
        numPeriods +
        // Add one period to represent the "immediate" deposit that occurs at
        // the recurrence start.
        1;

    // Lastly, if final recurring deposit would end before target date,
    // calculate PV of targetAmountDiff on the day of the last recurring deposit.

    const lastDepositDate = dt(recurrenceStartDate)
        .add(numPeriods * interval, unit)
        .toDateStr();
    const daysToTargetFromLastDeposit = getNumPeriods({
        startDate: lastDepositDate,
        targetDate,
        unit: 'day',
    });
    if (daysToTargetFromLastDeposit) {
        targetAmountDiff = PV({
            futureBalance: targetAmountDiff,
            interestRate: dailyInterestRate,
            numPeriods: daysToTargetFromLastDeposit,
        });
    }

    return Math.ceil(
        PmtAnnuityDue({
            futureValue: targetAmountDiff,
            interestRate: periodReturn,
            numPeriods: numDeposits,
        })
    );
};
